import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import MainLayout from "../components/layout/MainLayout";
import dashboardService from "../services/dashboardService";
import { useNotification } from "../context/NotificationContext";
import { Link } from "react-router-dom";
import AppButton from "../components/ui/AppButton";
import { createI18nFormatters } from "../utils/formatters";

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation(['dashboard', 'common']);
  const formatters = createI18nFormatters(i18n.language);
  const { showError } = useNotification();

  const fetchDashboardData = useCallback(async () => {
    console.log("fetchDashboardData called");
    try {
      setLoading(true);
      const response = await dashboardService.getDashboardSummary();

      if (response.success) {
        setDashboardData(response.data);
      }
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      showError(t('dashboard:messages.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [showError, t]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const statsConfig = [
    {
      title: t('dashboard:stats.users'),
      value: dashboardData?.totalUsers,
      icon: "fas fa-users",
      iconColor: "primary",
      path: "/users",
    },
    {
      title: t('dashboard:stats.universities'),
      value: dashboardData?.totalUniversities,
      icon: "fas fa-university",
      iconColor: "success",
      path: "/universities",
    },
    {
      title: t('dashboard:stats.clubs'),
      value: dashboardData?.totalClubs,
      icon: "fas fa-user-friends",
      iconColor: "info",
      path: "/clubs",
    },
    {
      title: t('dashboard:stats.campuses'),
      value: dashboardData?.totalCampuses,
      icon: "fas fa-building",
      iconColor: "warning",
      path: "/campuses",
    },
    {
      title: t('dashboard:stats.events'),
      value: dashboardData?.totalEvents,
      icon: "fas fa-calendar-alt",
      iconColor: "danger",
      path: "/events",
    },
  ];

  const navigationCards = [
    {
      title: t('dashboard:navigationCards.faculties.title'),
      description: t('dashboard:navigationCards.faculties.description'),
      icon: "fas fa-graduation-cap",
      iconColor: "purple",
      path: "/faculties",
    },
    {
      title: t('dashboard:navigationCards.departments.title'),
      description: t('dashboard:navigationCards.departments.description'),
      icon: "fas fa-building",
      iconColor: "teal",
      path: "/departments",
    },
    {
      title: t('dashboard:navigationCards.countries.title'),
      description: t('dashboard:navigationCards.countries.description'),
      icon: "fas fa-globe",
      iconColor: "indigo",
      path: "/countries",
    },
    {
      title: t('dashboard:navigationCards.cities.title'),
      description: t('dashboard:navigationCards.cities.description'),
      icon: "fas fa-city",
      iconColor: "orange",
      path: "/cities",
    },
    {
      title: t('dashboard:navigationCards.categories.title'),
      description: t('dashboard:navigationCards.categories.description'),
      icon: "fas fa-tags",
      iconColor: "pink",
      path: "/categories",
    },
  ];

  return (
    <MainLayout>
      <div className="dashboard-luxury">
        <div className="dashboard-hero">
          {/* Page Header - Clean & Professional */}
          <div className="text-center mb-4">
            <div className="dashboard-welcome-badge mb-3">
              <i className="fas fa-chart-line me-2"></i>
              {t('dashboard:welcome')}
            </div>
            <h1 className="dashboard-title mb-3">{t('dashboard:platformOverview')}</h1>
            <p className="dashboard-subtitle mb-4">
              {t('dashboard:subtitle')}
            </p>
            <AppButton
              variant="outline"
              onClick={fetchDashboardData}
              disabled={loading}
              className="btn-luxury"
            >
              <i className="fas fa-sync-alt me-2"></i>
              {t('common:buttons.refresh')}
            </AppButton>
          </div>

          {/* Statistics Cards - Professional Layout */}
          <div className="stats-grid mb-5">
            {statsConfig.map((stat, index) => (
              <Link
                key={index}
                to={stat.path}
                className="text-decoration-none"
              >
                <div 
                  className="luxury-stat-card" 
                  style={{ '--delay': index * 50 }}
                >
                  <div 
                    className="luxury-stat-card-inner"
                    style={{ color: stat.iconColor === 'primary' ? '#2563eb' : 
                                    stat.iconColor === 'success' ? '#16a34a' : 
                                    stat.iconColor === 'info' ? '#0ea5e9' : 
                                    stat.iconColor === 'warning' ? '#f59e0b' : '#dc2626' }}
                  >
                    <div className="stat-icon-wrapper">
                      <div className={`stat-icon bg-gradient-${stat.iconColor}`}>
                        {loading ? (
                          <div className="spinner-border spinner-border-sm text-white" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          <i className={stat.icon}></i>
                        )}
                      </div>
                    </div>
                    <div className="stat-content">
                      <div className="stat-label">{stat.title}</div>
                      <div className="stat-value">
                        {loading ? (
                          <div className="placeholder-glow">
                            <span className="placeholder col-8"></span>
                          </div>
                        ) : (
                          formatters.formatNumber(stat.value || 0)
                        )}
                      </div>
                    </div>
                    <div className="stat-arrow">
                      <i className="fas fa-arrow-right"></i>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Management Tools Section */}
          <div className="management-section">
            <div className="text-center mb-4">
              <h2 className="section-title mb-2">{t('dashboard:quickAccess.title')}</h2>
              <p className="section-subtitle">
                {t('dashboard:quickAccess.subtitle')}
              </p>
            </div>
            
            <div className="management-grid">
              {navigationCards.map((card, index) => (
                <Link
                  key={index}
                  to={card.path}
                  className="text-decoration-none"
                >
                  <div 
                    className="luxury-nav-card" 
                    style={{ '--delay': index * 50 }}
                  >
                    <div 
                      className="luxury-nav-card-inner"
                      style={{ color: card.iconColor === 'purple' ? '#7c3aed' : 
                                      card.iconColor === 'teal' ? '#14b8a6' : 
                                      card.iconColor === 'indigo' ? '#6366f1' : 
                                      card.iconColor === 'orange' ? '#f97316' : '#ec4899' }}
                    >
                      <div className="nav-card-icon-wrapper">
                        <div className={`nav-card-icon bg-gradient-${card.iconColor}`}>
                          <i className={card.icon}></i>
                        </div>
                      </div>
                      <div className="nav-card-content">
                        <h5 className="nav-card-title">{card.title}</h5>
                        <p className="nav-card-description">{card.description}</p>
                      </div>
                      <div className="nav-card-arrow">
                        <i className="fas fa-arrow-right"></i>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Dashboard;
